import type {
  Ad as PlayerAd,
  AdBreak as PlayerAdBreak,
  Event as PlayerEvent,
  GoogleImaAd as PlayerGoogleImaAd,
  LinearAd as PlayerLinearAd,
} from 'theoplayer';

import type {AdPosition} from '../../../api/AdapterAPI';
import type {AdBreakInfo} from '../../../types/ads/AdBreakInfo';
import type {AdInfo} from '../../../types/ads/AdInfo';
import {isFiniteNumber} from '../../../utils/numberUtils';
import {isBlank} from '../../../utils/stringUtils';
import {isNotNullish, isNullish} from '../../../utils/Utils';
import {CSAI_INTEGRATIONS, PlayerAdErrorEvent} from '../THEOplayerTypes';

export function mapTheoAd(ad: PlayerAd): AdInfo {
  const isLinear = ad.type === 'linear';

  const adInfo: AdInfo = {
    id: ad.id,
    isLinear,
    isSlate: ad.isSlate,
    clickThroughUrl: ad.clickThrough,
    width: ad.width,
    height: ad.height,
  };

  const linearAd = isLinear ? (ad as PlayerLinearAd) : undefined;
  if (isNotNullish(linearAd)) {
    adInfo.mediaUrl = linearAd.mediaUrl;
  }

  if (isNotNullish(ad.skipOffset)) {
    adInfo.skippable = ad.skipOffset !== -1;
    adInfo.skippableAfter = ad.skipOffset;
  }

  if (isFiniteNumber(ad.duration)) {
    adInfo.durationInSec = ad.duration;
  }

  const isGoogleIma = ad.integration === 'google-ima' || ad.integration === 'google-dai';
  const googleAd = isGoogleIma ? (ad as PlayerGoogleImaAd) : undefined;
  if (isNotNullish(googleAd)) {
    adInfo.bitrate = isNotNullish(googleAd.bitrate) ? googleAd.bitrate / 1000 : undefined;
    adInfo.mimeType = googleAd.contentType;
  }

  adInfo.vastData = {
    adSystem: ad.adSystem,
    creativeId: ad.creativeId,
  };

  const universalAdId = ad.universalAdIds && ad.universalAdIds.length > 0 ? ad.universalAdIds[0] : undefined;
  if (isNotNullish(universalAdId)) {
    adInfo.vastData.universalAdIdRegistry = universalAdId.adIdRegistry;
    adInfo.vastData.universalAdIdValue = universalAdId.adIdValue;
  }

  if (isNotNullish(googleAd)) {
    adInfo.vastData.apiFramework = googleAd.apiFramework;
    adInfo.vastData.adDescription = googleAd.description;
    adInfo.vastData.adTitle = googleAd.title;
    adInfo.vastData.wrapperAdsCount = isNotNullish(googleAd.wrapperAdIds) ? googleAd.wrapperAdIds.length : undefined;
  }

  if (ad.integration === 'mediakind') {
    /** The structure of the customData is defined by mediakind/dolby, see:
     * https://bitmovin.slack.com/archives/C0A9BRS15G9/p1782210055312149?thread_ts=1782205069.106959&cid=C0A9BRS15G9
     */
    const customData = ad.customData as {creativeAdId?: string; title?: string};
    adInfo.vastData.adTitle = customData?.title;
    adInfo.vastData.creativeAdId = customData?.creativeAdId;
  }

  return adInfo;
}

export function mapTheoAdBreak(adBreak: PlayerAdBreak, videoDuration?: number): AdBreakInfo {
  const timeOffset = adBreak.timeOffset;
  if (!isFiniteNumber(timeOffset) || timeOffset < 0) {
    return {position: undefined, scheduleTime: undefined};
  }

  if (timeOffset === 0) {
    return {position: 'pre', scheduleTime: 0};
  }

  // checks if timeOffset (aka ad schedule time) is close to video duration (aka end of video)
  // the 1sec delta threshold is an arbitrary number that accounts for small errors in the reported numbers.
  const DELTA_THRESHOLD_SEC = 1;
  const deltaTimeOffsetVideoDuration = isNotNullish(videoDuration) ? Math.abs(timeOffset - videoDuration) : undefined;
  if (isNotNullish(deltaTimeOffsetVideoDuration) && deltaTimeOffsetVideoDuration <= DELTA_THRESHOLD_SEC) {
    return {position: 'post', scheduleTime: videoDuration};
  }

  // default to midroll
  return {position: String(timeOffset), scheduleTime: timeOffset};
}

export function isAdErrorEvent(event: PlayerEvent<'aderror'>): event is PlayerAdErrorEvent {
  return isNotNullish(event) && 'errorCode' in event;
}

/**
 * Maps the position of a mapped {@link AdBreakInfo} (as produced by {@link mapTheoAdBreak})
 * to the {@link AdPosition} understood by the SSAI pipeline.
 */
export function toSsaiAdPosition(position: string | undefined): AdPosition | undefined {
  if (position === 'pre') {
    return 'preroll';
  }
  if (position === 'post') {
    return 'postroll';
  }
  // mapTheoAdBreak returns String(timeOffset) for midroll breaks, undefined for unknown
  return isNotNullish(position) ? 'midroll' : undefined;
}

export function isSsaiIntegration(integration: string | undefined): boolean {
  if (isNullish(integration) || isBlank(integration)) {
    return false;
  }

  return !CSAI_INTEGRATIONS.some((csai) => csai === integration);
}
